<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import Button from '../../Button.svelte';
  import EmployeeDonationService, { type ExtendedCharity } from '../../../lib/api/employee/donation.service';

  export let open: boolean = false;
  export let currentCharityUuid: string = '';
  export let currentCharityName: string = '';
  export let donationUuid: string = '';

  const dispatch = createEventDispatcher();

  let charities: ExtendedCharity[] = [];
  let selectedCharityUuid: string = '';
  let searchQuery: string = '';
  let loading: boolean = false;
  let loadingCharities: boolean = false;
  let error: string = '';
  let searchTimeout: ReturnType<typeof setTimeout>;

  // Limit initial display
  const INITIAL_DISPLAY_LIMIT = 6;
  let showingAll = false;

  // Show limited charities or all based on search/display state
  $: displayedCharities = showingAll || searchQuery 
    ? charities.filter(c => c.uuid !== currentCharityUuid)
    : charities.filter(c => c.uuid !== currentCharityUuid).slice(0, INITIAL_DISPLAY_LIMIT);
  $: hasMoreCharities = !searchQuery && charities.filter(c => c.uuid !== currentCharityUuid).length > INITIAL_DISPLAY_LIMIT;

  $: if (open) {
    loadCharities();
    selectedCharityUuid = '';
    error = '';
    showingAll = false;
  }

  async function loadCharities(query?: string) {
    loadingCharities = true;
    try {
      const result = await EmployeeDonationService.getCharities({ 
        phrase: query,
        verified: true,
        per_page: 100
      });
      if (result.success && result.data) {
        charities = result.data;
      }
    } catch (err) {
      console.error('Failed to load charities:', err);
    } finally {
      loadingCharities = false;
    }
  }

  function handleSearch(event: Event) {
    const target = event.target as HTMLInputElement;
    searchQuery = target.value;
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    searchTimeout = setTimeout(() => {
      loadCharities(searchQuery);
    }, 500);
  }

  function selectCharity(charityUuid: string) {
    selectedCharityUuid = charityUuid;
  }

  function showMoreCharities() {
    showingAll = true;
  }

  async function handleSubmit() {
    if (!selectedCharityUuid) {
      error = 'Please select a charity';
      return;
    }

    loading = true;
    error = '';

    try {
      const result = await EmployeeDonationService.updateDonationRequest(donationUuid, {
        charity_uuid: selectedCharityUuid
      });

      if (result.success) {
        dispatch('success', { message: 'Charity updated successfully' });
        handleClose();
      } else {
        error = result.message || 'Failed to update charity';
      }
    } catch (err: any) {
      error = err.message || 'An unexpected error occurred';
    } finally {
      loading = false;
    }
  }

  function handleClose() {
    if (!loading) {
      selectedCharityUuid = '';
      searchQuery = '';
      error = '';
      showingAll = false;
      dispatch('close');
    }
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && !loading) {
      handleClose();
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if open}
  <div 
    class="modal-backdrop" 
    role="presentation"
    on:click|self={handleClose}
  >
    <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="edit-charity-title">
      <div class="modal-header">
        <h2 id="edit-charity-title" class="modal-title">Change Charity</h2>
        <button 
          class="modal-close" 
          on:click={handleClose} 
          disabled={loading}
          aria-label="Close modal"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="current-charity">
        <span class="current-label">Current charity:</span>
        <span class="current-name">{currentCharityName}</span>
      </div>

      {#if error}
        <div class="error-message" role="alert">{error}</div>
      {/if}

      <div class="search-container">
        <div class="search-input-wrapper">
          <input
            type="text"
            placeholder="Search charities..."
            bind:value={searchQuery}
            on:input={handleSearch}
            class="search-input"
            disabled={loading}
          />
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
        </div>
      </div>

      <div class="charity-list">
        {#if loadingCharities}
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading charities...</p>
          </div>
        {:else if displayedCharities.length === 0}
          <div class="empty-state">
            <p>No other charities found</p>
            {#if searchQuery}
              <p class="empty-subtitle">Try adjusting your search</p>
            {/if}
          </div>
        {:else}
          {#each displayedCharities as charity (charity.uuid)}
            <button
              type="button"
              class="charity-item"
              class:selected={selectedCharityUuid === charity.uuid}
              on:click={() => selectCharity(charity.uuid)}
              disabled={loading}
            >
              <div class="charity-logo">
                {#if charity.logo}
                  <img src={charity.logo} alt="{charity.name} logo" />
                {:else}
                  <div class="charity-logo-placeholder">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  </div>
                {/if}
              </div>
              <div class="charity-info">
                <div class="charity-name-row">
                  <span class="charity-name">{charity.name}</span>
                  {#if charity.verified}
                    <div class="verified-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                    </div>
                  {/if}
                </div>
                {#if charity.category?.name}
                  <span class="charity-category">{charity.category.name}</span>
                {/if}
              </div>
              {#if selectedCharityUuid === charity.uuid}
                <div class="selected-indicator">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="20,6 9,17 4,12"/>
                  </svg>
                </div>
              {/if}
            </button>
          {/each}

          {#if hasMoreCharities && !showingAll}
            <button class="show-more-button" on:click={showMoreCharities} disabled={loading}>
              Show {charities.filter(c => c.uuid !== currentCharityUuid).length - INITIAL_DISPLAY_LIMIT} more charities
            </button>
          {/if}
        {/if}
      </div>

      <div class="modal-actions">
        <Button 
          variant="primary" 
          fullWidth={true}
          on:click={handleSubmit}
          disabled={loading || !selectedCharityUuid}
        >
          {loading ? "Updating..." : "Update Charity"}
        </Button>
        <Button 
          variant="secondary" 
          fullWidth={true}
          on:click={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(31, 41, 55, 0.5);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  .modal-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    width: 100%;
    max-width: 500px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 1.5rem 1rem;
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .modal-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    color: #6b7280;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-close:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #374151;
  }

  .modal-close:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .current-charity {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background-color: #f3f4f6;
    margin: 0 1.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
  }

  .current-label {
    color: #6b7280;
  }

  .current-name {
    color: #111827;
    font-weight: 500;
  }

  .error-message {
    font-size: 0.875rem;
    color: #ef4444;
    background-color: #fef2f2;
    border: 1px solid #fee2e2;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin: 0 1.5rem 1rem;
  }

  .search-container {
    padding: 0 1.5rem;
    margin-bottom: 1rem;
  }

  .search-input-wrapper {
    position: relative;
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 2.5rem 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 0.875rem;
    background-color: #fff;
    transition: all 0.2s ease;
  }

  .search-input:focus {
    outline: none;
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }

  .search-input::placeholder {
    color: #9ca3af;
  }

  .search-input:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }

  .search-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    pointer-events: none;
  }

  .charity-list {
    flex: 1;
    overflow-y: auto;
    padding: 0 1.5rem;
    margin-bottom: 1rem;
    min-height: 200px;
    max-height: 300px;
  }

  .charity-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 0.5rem;
    text-align: left;
  }

  .charity-item:hover:not(:disabled) {
    border-color: #d1d5db;
    background-color: #f9fafb;
  }

  .charity-item.selected {
    border-color: #f59e0b;
    background-color: #fffbeb;
  }

  .charity-item:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .charity-logo {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
    background-color: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .charity-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .charity-logo-placeholder {
    color: #6b7280;
  }

  .charity-info {
    flex: 1;
    min-width: 0;
  }

  .charity-name-row {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .charity-name {
    font-size: 0.9375rem;
    font-weight: 500;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .verified-badge {
    width: 16px;
    height: 16px;
    background-color: #10b981;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .verified-badge svg {
    color: white;
  }

  .charity-category {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .selected-indicator {
    width: 24px;
    height: 24px;
    background-color: #f59e0b;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .selected-indicator svg {
    color: white;
  }

  .show-more-button {
    width: 100%;
    background: none;
    border: none;
    color: var(--color-primary, #f59e0b);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0.75rem;
    margin-top: 0.5rem;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .show-more-button:hover:not(:disabled) {
    background-color: #fffbeb;
  }

  .show-more-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .loading-state,
  .empty-state {
    text-align: center;
    padding: 2rem 1rem;
    color: #6b7280;
  }

  .loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #f59e0b;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 0.75rem;
  }

  .empty-subtitle {
    font-size: 0.8125rem;
    margin-top: 0.25rem;
    opacity: 0.8;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 1.5rem 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  @media (max-width: 768px) {
    .modal-card {
      max-height: 90vh;
    }

    .charity-list {
      max-height: 250px;
    }
  }
</style>
